import { Avatar, Box, Button, Chip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { dimGrey, lightBackground1, lightBlack } from '../../mainTheme';
import { useNavigate, useParams } from 'react-router-dom';
import { faBarsFilter } from '@fortawesome/pro-light-svg-icons';
import DataGridTable from '../Shared/DataGridTable/DataGridTable';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCustomer,
  getCustomerCareTeam,
} from '../../store/customers/actionCreators';
import { RootState } from '../../store';
import { isNil } from 'lodash';
import LoadingSpinner from '../Shared/LoadingSpinner';
import {
  getCareTeamRoleById,
  getCareTeamRoleColorById,
  parseDateToLocal,
} from '../../shared/utils';
import { editOwner } from '../../store/hubOwner/actionCreators';
import * as Yup from 'yup';
import { Field, Form, Formik, FormikProps } from 'formik';
import MaskedInput from '../Shared/MaskedInput';
import { TextInputFormField } from '../Shared/TextInputFormField';
import ActionCell from '../Shared/ActionCell';
import { CARETEAM_ROLE } from '../../shared/constants';
import { toast } from 'react-toastify';

const baseStyle = {
  color: '#000',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.2px',
};

const headingStyle = {
  ...baseStyle,
  fontSize: '22px',
  lineHeight: '30px',
  letterSpacing: '0.36px',
};

const headLineStyle = {
  ...baseStyle,
  fontSize: '18px',
};

const contentStyle = {
  ...baseStyle,
  color: dimGrey,
  fontSize: '16px',
  paddingBottom: 2,
};

const stateSelector = (state: RootState) => {
  const { customers } = state;

  const { careTeam, isLoading, facilityCustomers } = customers;
  return {
    careTeam,
    isLoading,
    facilityCustomers,
  };
};

const validationSchema = Yup.object().shape({
  phone: Yup.string()
    .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number is not valid')
    .required('Phone number is required'),
  address1: Yup.string().required('Address is required'),
});

const HubOwnerProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { careTeam, isLoading, facilityCustomers } = useSelector<
    RootState,
    ReturnType<typeof stateSelector>
  >(stateSelector);

  const { isLoading: isUpdatingOwner } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  const [isEditing, setIsEditing] = useState(false);
  const formikRef =
    useRef<FormikProps<{ phone: string; address1: string }>>(null);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleActionNavigation = (
    careTeamRoleId: number,
    customerId: number,
  ) => {
    if (
      careTeamRoleId === CARETEAM_ROLE.PROFESSIONAL_CAREGIVER ||
      careTeamRoleId === CARETEAM_ROLE.FACILITY_SCHEDULER
    ) {
      navigate(`/v2/caregivers/${customerId}`);
    }
  };

  const handleSaveClick = async () => {
    const values = formikRef?.current?.values;
    const initial = formikRef?.current?.initialValues;
    // Send only changed fields: for email-less patients the server rejects
    // any payload that merely CONTAINS phone (identity tiebreaker) with a
    // 403, so an untouched phone must not ride along on an address edit
    const changed: { phone?: string; address1?: string } = {};
    if (values?.phone !== initial?.phone) changed.phone = values?.phone || '';
    if (values?.address1 !== initial?.address1)
      changed.address1 = values?.address1 || '';
    try {
      if (Object.keys(changed).length > 0) {
        await dispatch(editOwner(Number(id), changed));
      }
      // Dispatch getCustomer after editOwner completes
      await dispatch(getCustomer(Number(id)));
      setIsEditing(false);
    } catch (err: any) {
      // Identity-locked fields (e.g. phone on an email-less patient) come
      // back as a 403 with an actionable "contact customer care" message
      if (err?.status === 403 && typeof err?.body === 'string') {
        toast.error(err.body);
      } else {
        toast.error('Failed to update owner details');
      }
    }
  };

  const ownerDetails = (facilityCustomers || []).find(
    (customer) => customer.customer_id === Number(id),
  );

  const columns = [
    { field: 'careTeam', headerName: 'Care Team', flex: 1 },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      renderCell: (params: any) => (
        <Box display="flex" alignItems="center">
          <Chip
            avatar={
              <Avatar
                sx={{
                  bgcolor: 'white',
                  border: `2px solid ${params.value.borderColor}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: params.value.borderColor,
                  }}
                >
                  {params.value.text.charAt(0)}
                </Typography>
              </Avatar>
            }
            label={
              <Typography sx={{ color: lightBlack }} variant="caption">
                {params.value.text}
              </Typography>
            }
            sx={{
              backgroundColor: params.value.color,
              color: 'white',
              '& .MuiChip-icon': {
                color: 'white',
              },
            }}
          />
        </Box>
      ),
    },
    { field: 'dateAdded', headerName: 'Date added', flex: 1 },
    {
      field: 'action',
      headerName: 'Action',
      renderCell: (params: any) => (
        <ActionCell
          handleNavigation={() =>
            handleActionNavigation(params.row.careTeamRoleId, params.row.id)
          }
        />
      ),
    },
  ];

  const rows =
    careTeam && typeof id === 'string' && careTeam[id]
      ? careTeam[id].map((owner) => ({
          id: owner.customer_id,
          careTeam: `${owner.first_name} ${owner.last_name}`,
          careTeamRoleId: owner.careteam_role_id,
          role: {
            color: getCareTeamRoleColorById(owner.careteam_role_id)
              .backgroundColor,
            borderColor: getCareTeamRoleColorById(owner.careteam_role_id)
              .borderColor,
            text: getCareTeamRoleById(owner.careteam_role_id),
          },
          dateAdded: owner.created_at
            ? parseDateToLocal(owner.created_at, 'EEE MMM dd yyyy')
            : 'N/A',
          action: faBarsFilter,
        }))
      : [];

  useEffect(() => {
    if (!ownerDetails && !isLoading) {
      dispatch(getCustomer(Number(id)));
    }
    if (id && isNil(careTeam?.[id])) {
      dispatch(getCustomerCareTeam(Number(id)));
    }
  }, [dispatch, id, isLoading, ownerDetails, careTeam]);

  if (
    !ownerDetails ||
    !id ||
    isNil(careTeam?.[id]) ||
    isLoading ||
    isUpdatingOwner
  ) {
    return <LoadingSpinner />;
  }

  return (
    <Box display="flex" flexDirection="column" bgcolor={lightBackground1} p={2}>
      <Box>
        <Button
          variant="text"
          color="secondary"
          onClick={handleBackClick}
          data-testid="back-button"
        >
          Back
        </Button>
      </Box>
      <Box py={2}>
        <Typography sx={headingStyle}>{ownerDetails?.name}</Typography>
        <Box>
          <Chip label="Hub owner" />
        </Box>
      </Box>
      <Box>
        <Typography sx={headLineStyle} py={2}>
          Schedule
        </Typography>
        <Box display="flex" alignItems="baseline" pb={2}>
          <Typography sx={headLineStyle} pr={2}>
            Contact details
          </Typography>
          <Button
            type="submit"
            variant="text"
            color="secondary"
            onClick={isEditing ? handleSaveClick : handleEditClick}
            sx={{ minWidth: 'auto', width: '80px' }}
          >
            {isEditing ? 'Save' : 'Edit'}
          </Button>
          {isEditing && (
            <Button
              variant="text"
              color="secondary"
              onClick={() => {
                setIsEditing(false);
              }}
              sx={{ minWidth: 'auto', width: '80px' }}
            >
              Cancel
            </Button>
          )}
        </Box>

        <Typography sx={headLineStyle}>Email</Typography>
        <Typography sx={contentStyle}>{ownerDetails?.email}</Typography>

        <Typography sx={headLineStyle}>Phone</Typography>
        {isEditing ? (
          <Formik
            innerRef={formikRef}
            initialValues={{
              phone: ownerDetails.phone || '',
              address1: ownerDetails.address1 || '',
            }}
            validationSchema={validationSchema}
            onSubmit={() => {}}
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Box pb={1} sx={{ width: '50%' }}>
                  <Field
                    name="phone"
                    format="(###) ###-####"
                    placeholder="(123)-456-7890"
                    component={MaskedInput}
                    variant="standard"
                    size="small"
                    sx={{
                      fontSize: 18,
                      fontWeight: 'bold',
                      mb: 1,
                      color: 'red',
                    }}
                  />
                </Box>

                <Typography sx={headLineStyle}>Address</Typography>
                <Box pb={1} sx={{ width: '50%' }}>
                  <Field
                    name="address1"
                    placeholder="1234 Main Street"
                    component={TextInputFormField}
                    variant="standard"
                    size="small"
                  />
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <>
            <Typography sx={contentStyle}>
              {ownerDetails?.phone || 'N/A'}
            </Typography>
            <Typography sx={headLineStyle}>Address</Typography>
            <Typography sx={contentStyle}>
              {ownerDetails?.address1 || 'N/A'}
            </Typography>
          </>
        )}

        <Typography sx={headLineStyle}>Hub activation date</Typography>
        <Typography sx={contentStyle}>
          {ownerDetails?.device_synced_at
            ? parseDateToLocal(ownerDetails?.device_synced_at, 'MMM dd, yyyy')
            : 'N/A'}
        </Typography>
      </Box>
      <DataGridTable
        rows={rows}
        columns={columns}
        pageSize={5}
        maxHeight="40vh"
      />
    </Box>
  );
};

export default HubOwnerProfile;
